import { useState } from "preact/hooks";
import { must, sb } from "../lib/supabase";
import { Badge, ErrorBox, Loading, Modal, fmtDate, formData, useAction, useLoad } from "../lib/ui";
import { DOC_TYPES } from "../lib/types";

const BUCKET = "provider-docs";
const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];
const MAX = 10 * 1024 * 1024;
const docLabel = Object.fromEntries(DOC_TYPES) as Record<string, string>;

/** Uploads a file to the private bucket under {org}/{provider}/ and records it. */
export async function uploadDocument(orgId: string, providerId: string, file: File, docType: string, credentialId?: string | null) {
  if (!ALLOWED.includes(file.type)) throw new Error("Please upload a PDF, JPG or PNG.");
  if (file.size > MAX) throw new Error("Files must be 10 MB or smaller.");
  const ext = file.type === "application/pdf" ? "pdf" : file.type === "image/png" ? "png" : "jpg";
  const path = `${orgId}/${providerId}/${crypto.randomUUID()}.${ext}`;
  const up = await sb().storage.from(BUCKET).upload(path, file, { contentType: file.type, upsert: false });
  if (up.error) throw new Error(up.error.message);
  const ins = await sb().from("provider_documents").insert({
    org_id: orgId, provider_id: providerId, doc_type: docType, credential_id: credentialId || null,
    file_name: file.name.slice(0, 200), storage_path: path, content_type: file.type, size_bytes: file.size,
  });
  if (ins.error) {
    await sb().storage.from(BUCKET).remove([path]); // don't leave orphaned files
    throw new Error(ins.error.message);
  }
}

/** Opens a document through a short-lived signed URL (5 minutes). */
export async function openDocument(path: string) {
  const { data, error } = await sb().storage.from(BUCKET).createSignedUrl(path, 300);
  if (error || !data) throw new Error(error?.message ?? "Could not open document");
  window.open(data.signedUrl, "_blank", "noopener,noreferrer");
}

export function UploadDocument({ orgId, providerId, creds = [], onDone, label = "Upload document" }:
  { orgId: string; providerId: string; creds?: any[]; onDone: () => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const { busy, run } = useAction();
  return (
    <>
      <button onClick={() => setOpen(true)}>{label}</button>
      {open && (
        <Modal title="Upload document" onClose={() => setOpen(false)}>
          <form class="grid2" onSubmit={(e) => {
            const f = formData(e);
            const file = ((e.currentTarget as HTMLFormElement).elements.namedItem("file") as HTMLInputElement).files?.[0];
            if (!file) return;
            run(async () => { await uploadDocument(orgId, providerId, file, f.doc_type!, f.credential_id); setOpen(false); onDone(); }, "Uploaded");
          }}>
            <label>Document type<select name="doc_type" required>{DOC_TYPES.map(([k, l]) => <option value={k}>{l}</option>)}</select></label>
            {creds.length > 0 && <label>Related credential (optional)<select name="credential_id"><option value="">—</option>
              {creds.map((c) => <option value={c.id}>{c.kind.replaceAll("_", " ")} {c.state ?? ""} {c.number ?? ""}</option>)}</select></label>}
            <label class="span2">File (PDF, JPG or PNG, max 10 MB)<input name="file" type="file" accept=".pdf,.jpg,.jpeg,.png" required /></label>
            <p class="span2 muted small">Do not upload documents showing Social Security numbers or any client information. Black them out first.</p>
            <div class="span2 row-end"><button disabled={busy}>{busy ? "Uploading…" : "Upload"}</button></div>
          </form>
        </Modal>
      )}
    </>
  );
}

export function DocumentList({ providerId, staff, isAdmin, refreshKey = 0 }: { providerId: string; staff: boolean; isAdmin: boolean; refreshKey?: number }) {
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () =>
    must(await sb().from("provider_documents").select("*").eq("provider_id", providerId).order("created_at", { ascending: false })) as any[],
  [providerId, refreshKey]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  if (data.length === 0) return <p class="muted">No documents yet.</p>;
  const review = (id: string, review_status: string) => run(async () => {
    const note = review_status === "rejected" ? prompt("Reason (shown to staff):") : null;
    must(await sb().from("provider_documents").update({ review_status, review_note: note }).eq("id", id));
    reload();
  }, `Marked ${review_status}`);

  return (
    <table>
      <thead><tr><th>Type</th><th>File</th><th>Uploaded</th><th>Review</th><th></th></tr></thead>
      <tbody>{data.map((d) => (
        <tr>
          <td>{docLabel[d.doc_type] ?? d.doc_type}</td>
          <td><button class="link" onClick={() => run(() => openDocument(d.storage_path))}>{d.file_name}</button>
            <div class="muted small">{Math.ceil(d.size_bytes / 1024)} KB</div></td>
          <td>{fmtDate(d.created_at)}</td>
          <td><Badge value={d.review_status} />{d.review_note && <div class="muted small">{d.review_note}</div>}</td>
          <td class="btns">
            {staff && d.review_status === "pending" && <>
              <button class="small ghost" disabled={busy} onClick={() => review(d.id, "accepted")}>Accept</button>
              <button class="small ghost" disabled={busy} onClick={() => review(d.id, "rejected")}>Reject</button></>}
            {isAdmin && <button class="small ghost danger" disabled={busy} onClick={() => confirm("Delete this document permanently?") && run(async () => {
              const rm = await sb().storage.from(BUCKET).remove([d.storage_path]);
              if (rm.error) throw new Error(rm.error.message);
              must(await sb().from("provider_documents").delete().eq("id", d.id));
              reload();
            }, "Deleted")}>Delete</button>}
          </td>
        </tr>))}</tbody>
    </table>
  );
}
